import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';

/**
 * Enhanced Admin Route Protection Component
 * Implements fail-secure route protection with comprehensive loading states
 * and security-first access control.
 */
export function AdminRoute({ children }) {
  const { loading, isAdmin, error } = useAdminAuth();
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Fail secure: if still loading or not mounted, show loading state
  if (!mounted || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying administrator access...</p>
          <p className="mt-2 text-sm text-gray-500">
            This may take a few moments while we validate your credentials
          </p>
        </div>
      </div>
    );
  }
  
  // Fail secure: if there's an authentication error, show error and redirect
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Error</h3>
          <p className="text-sm text-gray-600 mb-4">
            There was an error verifying your administrator access.
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }
  
  // Fail secure: if not admin, redirect to login
  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }
  
  // Success: render protected content
  return children;
}

export default AdminRoute;
